import { motion } from "framer-motion";
import { ArrowUpRight, Droplets, Fuel, Package, ShieldCheck, Sparkles, Timer, Wind } from "lucide-react";
import { BUSINESS } from "@/constants/business";

const IMG = {
    diagnostics:
        "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxtZWNoYW5pYyUyMHJlcGFpcmluZyUyMGNhciUyMGVuZ2luZXxlbnwwfHx8fDE3OTA1NjQzNzZ8MA&ixlib=rb-4.1.0&q=85",
    brakes:
        "https://images.unsplash.com/photo-1597137257188-5916d281a9e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjYXIlMjB3aGVlbCUyMGJyYWtlJTIwY2FsaXBlcnxlbnwwfHx8fDE3OTA1NjQzNzZ8MA&ixlib=rb-4.1.0&q=85",
    fluids:
        "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxtZWNoYW5pYyUyMHJlcGFpcmluZyUyMGNhciUyMGVuZ2luZXxlbnwwfHx8fDE3OTA1NjQzNzZ8MA&ixlib=rb-4.1.0&q=85",
    service:
        "https://images.unsplash.com/photo-1542377281-73d08e3a10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjB3aGVlbCUyMGJyYWtlJTIwY2FsaXBlcnxlbnwwfHx8fDE3OTA1NjQzNzZ8MA&ixlib=rb-4.1.0&q=85",
};

export const SERVICES = [
    {
        name: "Brake System Overhaul",
        description:
            "Three workshop-grade packages done on your driveway — all labour at $100/hr, parts quoted upfront.",
        badge: "CRITICAL SAFETY",
        tiers: [
            { name: "Deluxe", price: 250, duration: "2.5 HRS", includes: "Pads, rotors, brake bleed & handbrake adjustment" },
            { name: "Standard", price: 200, duration: "2 HRS", includes: "Pads, rotors & brake bleed" },
            { name: "Basic", price: 150, duration: "1.5 HRS", includes: "Pads & rotors" },
        ],
        image: IMG.brakes,
        span: "lg:col-span-8",
    },
    {
        name: "Mobile Diagnostics",
        description:
            "Full CAN-bus scan, check-engine code diagnostics and live sensor telemetry from our mobile lab unit.",
        badge: "MOST POPULAR",
        price: "$200 fee",
        duration: "APPROX 1 HR",
        policy: "No diagnostic outcome? It's free.",
        image: IMG.diagnostics,
        span: "lg:col-span-4",
    },
    {
        name: "Oil Change & Fluids",
        description:
            "Quality oil change with filter, fluid top-ups and a quick safety once-over while you get on with your day.",
        badge: "MAINTENANCE",
        price: "From $50",
        duration: "30 MIN ON-SITE",
        image: IMG.fluids,
        span: "lg:col-span-6",
    },
    {
        name: "Standard Vehicle Service",
        description:
            "Complete vehicle service — inspection, plugs, fluids and safety check at your home, work or farm gate.",
        badge: "FULL SERVICE",
        price: "From $150",
        duration: "APPROX 2 HRS",
        image: IMG.service,
        span: "lg:col-span-6",
    },
    {
        name: "Injector Cleaner",
        description: "Fuel system injector clean to restore spray pattern, idle and throttle response.",
        badge: "QUICK ADD-ON",
        price: "$25 + part",
        duration: "15 MIN",
        icon: "fuel",
        compact: true,
        span: "lg:col-span-3",
    },
    {
        name: "Anti-Bacterial Cleanse",
        description: "Cabin and A/C system anti-bacterial treatment — kills odours at the source.",
        badge: "QUICK ADD-ON",
        price: "$25 + part",
        duration: "15 MIN",
        icon: "sparkles",
        compact: true,
        span: "lg:col-span-3",
    },
    {
        name: "Induction Cleanse",
        description: "Intake and induction carbon clean for smoother running and better economy.",
        badge: "QUICK ADD-ON",
        price: "$25 + part",
        duration: "15 MIN",
        icon: "wind",
        compact: true,
        span: "lg:col-span-3",
    },
    {
        name: "Oil Flush",
        description: "Engine oil flush before the fresh oil goes in — clears sludge and old residue.",
        badge: "QUICK ADD-ON",
        price: "$25 + part",
        duration: "15 MIN",
        icon: "droplets",
        compact: true,
        span: "lg:col-span-3",
    },
    {
        name: "Full Cleanse Package",
        description:
            "All four treatments in one visit — injector cleaner, anti-bacterial cleanse, induction cleanse and oil flush. The complete reset for your engine and cabin.",
        badge: "BUNDLE — SAVE $20",
        price: "$80 + parts",
        duration: "APPROX 1 HR",
        icon: "package",
        compact: true,
        span: "lg:col-span-12",
    },
];

const ICONS = { fuel: Fuel, sparkles: Sparkles, wind: Wind, droplets: Droplets, package: Package };

export default function Services({ onBook }) {
    return (
        <section id="services" data-testid="services-section" className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-16 max-w-2xl"
                >
                    <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">
                        01 / Field Services
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-tight">
                        The workshop comes to you
                    </h2>
                </motion.div>

                <div className="grid lg:grid-cols-12 gap-6" data-testid="services-bento-grid">
                    {SERVICES.map((service, i) => {
                        const Icon = service.icon ? ICONS[service.icon] : null;
                        return (
                            <motion.article
                                key={service.name}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
                                whileHover={{ y: -6 }}
                                data-testid={`service-card-${i + 1}`}
                                className={`group relative rounded-2xl overflow-hidden border border-[#22262F] bg-[#121418] hover:border-amber-500/50 transition-colors duration-300 ${service.span}`}
                            >
                                {!service.compact && (
                                    <div className="relative h-52 sm:h-60 overflow-hidden">
                                        <img
                                            src={service.image}
                                            alt={service.name}
                                            className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#121418] via-[#121418]/30 to-transparent" />
                                        <span className="absolute top-4 left-4 text-[10px] font-mono uppercase tracking-[0.2em] bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full px-3 py-1">
                                            {service.badge}
                                        </span>
                                    </div>
                                )}
                                <div className={service.compact ? "p-6" : "p-6 sm:p-8"}>
                                    {service.compact && (
                                        <div className="flex items-center justify-between mb-5">
                                            <span className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                                                {Icon && <Icon className="w-5 h-5 text-amber-400" />}
                                            </span>
                                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400">
                                                {service.badge}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-start justify-between gap-4">
                                        <h3 className={`font-display font-bold uppercase tracking-wide text-zinc-100 leading-tight ${service.compact ? "text-xl" : "text-2xl"}`}>
                                            {service.name}
                                        </h3>
                                        {!service.tiers && (
                                            <div className="flex flex-col items-end gap-2 shrink-0">
                                                <span className="text-xs font-mono text-amber-400 border border-amber-500/30 rounded-full px-3 py-1 whitespace-nowrap">
                                                    {service.price}
                                                </span>
                                                {service.duration && (
                                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">
                                                        <Timer className="w-3 h-3 text-amber-500" />
                                                        {service.duration}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{service.description}</p>

                                    {service.policy && (
                                        <p className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-amber-400" data-testid="diagnostics-policy">
                                            <ShieldCheck className="w-4 h-4" /> {service.policy}
                                        </p>
                                    )}

                                    {service.tiers ? (
                                        <div className="mt-6 space-y-3" data-testid="brake-tiers">
                                            {service.tiers.map((tier) => (
                                                <div
                                                    key={tier.name}
                                                    data-testid={`brake-tier-${tier.name.toLowerCase()}`}
                                                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#22262F] bg-[#0B0C0E] px-5 py-4 hover:border-amber-500/40 transition-colors duration-200"
                                                >
                                                    <div className="min-w-[200px]">
                                                        <div className="font-display text-lg font-bold uppercase text-white">
                                                            {tier.name}
                                                            <span className="ml-3 text-xs font-mono text-zinc-500 normal-case tracking-normal">
                                                                {tier.includes}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-xs font-mono text-amber-400 whitespace-nowrap">
                                                            ${tier.price} + parts
                                                        </span>
                                                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">
                                                            <Timer className="w-3 h-3 text-amber-500" />
                                                            {tier.duration}
                                                        </span>
                                                        <button
                                                            onClick={() => onBook(`${service.name} — ${tier.name}`)}
                                                            data-testid={`brake-book-${tier.name.toLowerCase()}`}
                                                            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-zinc-300 hover:text-amber-400 transition-colors duration-200"
                                                        >
                                                            Book <ArrowUpRight className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => onBook(service.name)}
                                            data-testid={`service-book-button-${i + 1}`}
                                            className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-amber-400 transition-colors duration-200"
                                        >
                                            Book this service
                                            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                                        </button>
                                    )}
                                </div>
                            </motion.article>
                        );
                    })}
                </div>

                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mt-8 text-xs font-mono uppercase tracking-[0.18em] text-zinc-500"
                    data-testid="services-labour-note"
                >
                    {BUSINESS.pricingNote}
                </motion.p>
            </div>
        </section>
    );
}
